import type { EnhancedSmartLink } from './smartLinkTypes.ts';
import { formatLabel } from './formatLabel.ts';
import { formatProduct } from './formatProduct.ts';

export function formatDaysAgo(dateString: string | undefined, locale: string): string | undefined {
	if (!dateString) {
		return undefined;
	}
	const date = new Date(dateString);
	if (isNaN(date.getTime())) {
		return undefined;
	}
	const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));

	if (diffDays < 0) {
		return undefined;
	}
	const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });
	if (diffDays < 7) {
		return rtf.format(-diffDays, 'day');
	}
	return rtf.format(-Math.floor(diffDays / 7), 'week');
}

export const getDescription = (
	item: EnhancedSmartLink,
	locale: string,
): (string | null | undefined)[] => {
	// Slot 1 — classification: "{Product} {resourceType}". Product uses the
	// customer-facing label (never the `townsquare` codename); resourceType is
	// lowercased. Either part is omitted when its source field is absent.
	const productLabel = formatProduct(item.product);
	const resourceTypeLabel = formatLabel(item.resourceType)?.toLowerCase();
	const parts = [productLabel, resourceTypeLabel].filter((part): part is string => Boolean(part));
	// Guard against duplicate words (e.g. townsquare "Goals" app → "Goals goals").
	const slot1 =
		parts.length === 2 && parts[0].toLowerCase() === parts[1].toLowerCase()
			? parts[0]
			: parts.join(' ') || undefined;

	// Slot 2 — person: who created, updated, or owns it.
	const slot2 = item.owner || item.createdBy || item.updatedBy || item.assignedTo;

	// Slot 3 — date: when it was last touched.
	const slot3 =
		formatDaysAgo(item.updatedAt, locale) || formatDaysAgo(item.createdAt, locale) || null;

	return [slot1, slot2, slot3];
};
